import SharedDialog from '../../../../shared/shared-dialog';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { BuildingsModel } from '../../../../../types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../../state/zustand-store';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { UserKeys, updateBuilding } from '../../../../api/users-api';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { MuiChipsInput } from 'mui-chips-input';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import PlacesAutocomplete from '../../../../shared/places-autocomplete';
import { Messages } from '../../../../utils/message-constants';
import { countryCodes, states } from '../../../../shared/geography-utils';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
interface EditBuildingDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  selectedBuilding: BuildingsModel;
}

export default function EditBuildingDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedBuilding,
}: EditBuildingDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [addBuildingAddress, setAddBuildingAddress] = useState(
    selectedBuilding.address ? true : false
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    watch,
    setValue,
  } = useForm<BuildingsModel>({
    values: {
      ...selectedBuilding,
      coordinates: selectedBuilding.coordinates
        ? selectedBuilding.coordinates
        : null,
      address: selectedBuilding.address
        ? { ...selectedBuilding.address }
        : null,
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: BuildingsModel) =>
      updateBuilding(data, selectedBuilding, changeLogMessage),
    onSuccess: (returnData) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Building'),
      });

      queryClient.setQueryData<BuildingsModel[] | undefined>(
        [UserKeys.BuildingsList],
        (oldData) => {
          if (oldData) {
            return oldData.map((building) =>
              building.buildingId === selectedBuilding.buildingId
                ? returnData
                : building
            );
          }
          return oldData;
        }
      );
      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('this building'),
      });
    },
    onSettled: () => {
      createEvent(analytics.calendars.resource_calendars.update_building, {
        module: ModuleList.modules.calendars.name,
        section: ModuleList.modules.calendars.sections.resourceCalendars,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const onSubmit: SubmitHandler<BuildingsModel> = (data) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.EditBuildings.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const newBuilding: BuildingsModel = {
        ...data,
      };
      mutate(newBuilding);
    }
  };

  const selectedCountry = watch('address.regionCode') || 'US';

  return (
    <SharedDialog
      maxWidth="md"
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title="Edit Building"
      dialogContent={
        <Stack gap={2}>
          <Grid container>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="buildingId"
                control={control}
                rules={{
                  required: 'Id is required.',
                }}
                render={({ field }) => (
                  <TextField
                    required
                    autoFocus
                    inputProps={{
                      id: 'buildingId',
                    }}
                    InputLabelProps={{
                      htmlFor: 'buildingId',
                    }}
                    label="Id"
                    error={!!errors.buildingId}
                    helperText={errors?.buildingId?.message || ' '}
                    {...field}
                    sx={{ width: 400 }}
                    autoComplete="off"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="buildingName"
                control={control}
                rules={{
                  required: 'Name is required.',
                }}
                render={({ field }) => (
                  <TextField
                    required
                    inputProps={{
                      id: 'buildingName',
                    }}
                    InputLabelProps={{
                      htmlFor: 'buildingName',
                    }}
                    label="Name"
                    error={!!errors.buildingName}
                    helperText={errors?.buildingName?.message || ' '}
                    {...field}
                    sx={{ width: 400 }}
                    autoComplete="off"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'description',
                    }}
                    InputLabelProps={{
                      htmlFor: 'description',
                    }}
                    label="Description"
                    multiline
                    rows={2}
                    {...field}
                    sx={{ width: 400 }}
                    autoComplete="off"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="floorNames"
                control={control}
                rules={{
                  required: 'You must have at least 1 floor.',
                }}
                render={({ field }) => (
                  <MuiChipsInput
                    {...field}
                    required
                    sx={{ width: 400 }}
                    clearInputOnBlur
                    disableEdition
                    error={!!errors.floorNames}
                    helperText={errors?.floorNames?.message || ' '}
                    label="Floors"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 7 }}>
              <Button
                size="small"
                onClick={() => {
                  if (addBuildingAddress) {
                    setValue('address', null);
                    setValue('coordinates', {
                      latitude: null,
                      longitude: null,
                    });
                  } else {
                    setValue('address', {
                      regionCode: 'US',
                      languageCode: null,
                      postalCode: '',
                      administrativeArea: '',
                      locality: '',
                      sublocality: '',
                      addressLines: [''],
                    });
                    setValue('coordinates', {
                      latitude: 0,
                      longitude: 0,
                    });
                  }
                  setAddBuildingAddress(!addBuildingAddress);
                }}
                sx={{ width: 'fit-content' }}
              >
                {addBuildingAddress
                  ? 'Remove Building Address'
                  : 'Add Building Address'}
              </Button>
            </Grid>
          </Grid>
          {addBuildingAddress ? (
            <Grid container>
              <Grid size={{ xs: 7 }}>
                <PlacesAutocomplete
                  control={control}
                  errors={errors}
                  onPlaceSelect={(props) => {
                    if (props?.geometry?.location) {
                      setValue(
                        'coordinates.latitude',
                        props.geometry.location.lat()
                      );
                      setValue(
                        'coordinates.longitude',
                        props.geometry.location.lng()
                      );
                    }
                    if (props?.address_components) {
                      const regionCode =
                        props.address_components.find((component) =>
                          component.types.includes('country')
                        )?.short_name || '';
                      setValue('address.regionCode', regionCode);
                      const streetNumber =
                        props.address_components.find((component) =>
                          component.types.includes('street_number')
                        )?.short_name || '';
                      const route =
                        props.address_components.find((component) =>
                          component.types.includes('route')
                        )?.short_name || '';
                      const streetAddress =
                        streetNumber.length > 0
                          ? streetNumber + ' ' + route
                          : route;
                      setValue('address.addressLines.0', streetAddress);
                      const locality =
                        props.address_components.find((component) =>
                          component.types.includes('locality')
                        )?.short_name ||
                        props.address_components.find((component) =>
                          component.types.includes('neighborhood')
                        )?.short_name ||
                        '';
                      setValue(`address.locality`, locality);
                      const postalCode =
                        props.address_components.find((component) =>
                          component.types.includes('postal_code')
                        )?.short_name || '';
                      setValue(`address.postalCode`, postalCode);
                      const sublocality =
                        props.address_components.find((component) =>
                          component.types.includes('sublocality')
                        )?.short_name || '';
                      setValue(`address.sublocality`, sublocality);
                      const administrativeArea =
                        props.address_components.find((component) =>
                          component.types.includes(
                            'administrative_area_level_1'
                          )
                        )?.short_name || '';
                      setValue(
                        `address.administrativeArea`,
                        administrativeArea
                      );
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Controller
                  name={'address.regionCode'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('address.administrativeArea', '');
                      }}
                      inputProps={{
                        id: 'country',
                      }}
                      InputLabelProps={{
                        htmlFor: 'country',
                      }}
                      required
                      label="Country"
                      sx={{ width: 400 }}
                      select
                    >
                      {countryCodes.map((country) => (
                        <MenuItem key={country.value} value={country.value}>
                          {country.displayValue}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              {selectedCountry === 'US' ? (
                <Grid size={{ xs: 6 }}>
                  <Controller
                    name="address.administrativeArea"
                    control={control}
                    rules={{
                      required: 'State is required.',
                    }}
                    render={({ field }) => (
                      <TextField
                        required
                        {...field}
                        inputProps={{
                          id: 'state',
                        }}
                        InputLabelProps={{
                          htmlFor: 'state',
                        }}
                        label="State"
                        sx={{ width: 400 }}
                        select
                        error={!!errors.address?.administrativeArea}
                        helperText={
                          errors?.address?.administrativeArea?.message || ' '
                        }
                      >
                        {states.map((state) => (
                          <MenuItem key={state.value} value={state.value}>
                            {state.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              ) : (
                <Grid size={{ xs: 6 }}>
                  <Controller
                    name="address.administrativeArea"
                    control={control}
                    rules={{
                      required: 'Province is required.',
                    }}
                    render={({ field }) => (
                      <TextField
                        required
                        {...field}
                        inputProps={{
                          id: 'province',
                        }}
                        InputLabelProps={{
                          htmlFor: 'province',
                        }}
                        error={!!errors.address?.administrativeArea}
                        helperText={
                          errors?.address?.administrativeArea?.message || ' '
                        }
                        label="Province"
                        sx={{ width: 400 }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 6 }}>
                <Controller
                  name="address.locality"
                  control={control}
                  rules={{
                    required: 'City is required.',
                  }}
                  render={({ field }) => (
                    <TextField
                      required
                      inputProps={{
                        id: 'locality',
                      }}
                      InputLabelProps={{
                        htmlFor: 'locality',
                      }}
                      error={!!errors.address?.locality}
                      helperText={errors?.address?.locality?.message || ' '}
                      label="City"
                      {...field}
                      sx={{ width: 400 }}
                      autoComplete="off"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Controller
                  name="address.postalCode"
                  control={control}
                  rules={{
                    required: 'Zip is required.',
                  }}
                  render={({ field }) => (
                    <TextField
                      required
                      inputProps={{
                        id: 'postalCode',
                      }}
                      InputLabelProps={{
                        htmlFor: 'postalCode',
                      }}
                      error={!!errors.address?.postalCode}
                      helperText={errors?.address?.postalCode?.message || ' '}
                      label="Zip Code"
                      {...field}
                      sx={{ width: 400 }}
                      autoComplete="off"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Controller
                  name="coordinates.longitude"
                  control={control}
                  render={({ field }) => (
                    <Tooltip
                      arrow
                      title={
                        'Will be filled out when selecting a result from the google maps autocomplete.'
                      }
                    >
                      <TextField
                        inputProps={{
                          id: 'longitude',
                        }}
                        InputLabelProps={{
                          htmlFor: 'longitude',
                        }}
                        disabled
                        type={'number'}
                        label="Longitude"
                        value={field.value || ''}
                        onChange={field.onChange}
                        sx={{ width: 400 }}
                        autoComplete="off"
                      />
                    </Tooltip>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Controller
                  name="coordinates.latitude"
                  control={control}
                  render={({ field }) => (
                    <Tooltip
                      arrow
                      title={
                        'Will be filled out when selecting a result from the google maps autocomplete.'
                      }
                    >
                      <TextField
                        inputProps={{
                          id: 'latitude',
                        }}
                        InputLabelProps={{
                          htmlFor: 'latitude',
                        }}
                        disabled
                        type={'number'}
                        label="Latitude"
                        value={field.value || ''}
                        onChange={field.onChange}
                        sx={{ width: 400 }}
                        autoComplete="off"
                      />
                    </Tooltip>
                  )}
                />
              </Grid>
            </Grid>
          ) : null}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const newBuilding: BuildingsModel = {
                ...getValues(),
              };
              mutate(newBuilding);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
